import { useCallback } from 'react';
import { message } from 'antd';
import { getUserDetails, makePostCall, removeContentById } from '../../../utils/helper';
import { STATUS_MAP } from '../constants';
import { extractReportParts, extractMedicalContent, updateHeaderOnSignoff } from '../utils/reportParser';
import type { OrderDetails, Report } from '../types';

interface UseReportActionsOptions {
  orderDetails: OrderDetails | null;
  currentReport: Report | null;
  isSignedOff: boolean;
  showFooter: boolean;
  onSaved?: (status: string) => void;
}

/**
 * Provides save, delete actions for the report editor.
 * Centralises the save flow:
 *   1. Strip cosign element
 *   2. Update header on signoff
 *   3. Extract header/body/footer
 *   4. POST to /submit-report
 */
export const useReportActions = ({
  orderDetails,
  currentReport,
  isSignedOff,
  showFooter,
  onSaved,
}: UseReportActionsOptions) => {
  const pacs_order = orderDetails?.pacs_order;
  const patient = pacs_order?.patient;

  const saveReport = useCallback(async (
    editorContent: string,
    statusCode: string,
    extraPayload: Record<string, any> = {},
  ) => {
    if (!pacs_order || !patient || !editorContent) return;

    let contentToSave = removeContentById(editorContent, 'cosign');

    if (statusCode === 'SIGNEDOFF') {
      contentToSave = updateHeaderOnSignoff(contentToSave);
    }

    const { header, body, footer } = extractReportParts(contentToSave);
    const backendStatus = STATUS_MAP[statusCode] || statusCode;

    await makePostCall('/submit-report', {
      html: body,
      header,
      footer: showFooter ? footer : '',
      yh_no: patient.pat_pin,
      order_no: pacs_order.po_his_ord_no,
      acc_no: pacs_order.po_acc_no,
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
      status: backendStatus,
      report_id: currentReport?.pr_id,
      is_editing_signedoff: isSignedOff,
      edited_at: isSignedOff ? new Date().toISOString() : undefined,
      edited_by: isSignedOff ? getUserDetails()?.username : undefined,
      ...extraPayload,
    });

    onSaved?.(backendStatus);
  }, [pacs_order, patient, currentReport, isSignedOff, showFooter, onSaved]);

  const deleteReport = useCallback(async (report: Report) => {
    if (!patient) return;
    await makePostCall('/delete-report', {
      report_id: report.pr_id,
      yh_no: patient.pat_pin,
      order_id: report.pr_pacs_ord_id,
    });
  }, [patient]);

  const printReport = useCallback(async (editorContent: string) => {
    if (!pacs_order || !patient || !editorContent) return;

    try {
      const medicalContent = extractMedicalContent(editorContent);
      const res = await makePostCall('/print-report', {
        report: currentReport,
        patDetails: orderDetails,
        html: medicalContent,
        order_id: pacs_order.pacs_ord_id,
      }, {
        responseType: 'arraybuffer',
      });

      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.print();
      }
    } catch (err) {
      console.error('Failed to print report:', err);
      message.error('Failed to print report');
    }
  }, [pacs_order, patient, currentReport, orderDetails]);

  return { saveReport, deleteReport, printReport };
};
